import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Share2, DollarSign, Users, Wallet, ArrowUpRight, Save, Loader2, Clock, ShieldAlert, FileText } from "lucide-react";
import api from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AffiliateDashboard() {
  const [me, setMe] = useState(null);
  const [refs, setRefs] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [methods, setMethods] = useState([]);
  const [details, setDetails] = useState({});
  const [savingPayout, setSavingPayout] = useState(false);
  const [minPayout, setMinPayout] = useState(50);
  const [requests, setRequests] = useState([]);
  const [requesting, setRequesting] = useState(false);
  const [acceptAgreement, setAcceptAgreement] = useState(false);
  const [applying, setApplying] = useState(false);
  const [reaccepting, setReaccepting] = useState(false);

  const loadRequests = () => api.get("/affiliate/me/payout-requests").then((x) => setRequests(x.data)).catch(() => {});

  const loadMe = () => api.get("/affiliate/me").then((r) => {
    setMe(r.data);
    if (r.data.is_affiliate) {
      api.get("/affiliate/me/referrals").then((x) => setRefs(x.data)).catch(() => {});
      api.get("/affiliate/me/payouts").then((x) => setPayouts(x.data)).catch(() => {});
      api.get("/affiliate/payout-methods").then((x) => { setMethods(x.data.methods || []); setDetails(x.data.details || {}); setMinPayout(x.data.min_payout ?? 50); }).catch(() => {});
      loadRequests();
    }
  }).catch(() => setMe({ is_affiliate: false }));

  useEffect(() => {
    loadMe().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = async () => {
    setApplying(true);
    try {
      await api.post("/affiliate/apply", { accept_agreement: true });
      toast.success("Application submitted — we'll review it shortly");
      await loadMe();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to apply"); }
    finally { setApplying(false); }
  };

  const reaccept = async () => {
    setReaccepting(true);
    try {
      await api.post("/affiliate/accept-agreement", {});
      toast.success("Thanks — agreement accepted");
      await loadMe();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setReaccepting(false); }
  };

  const requestPayout = async () => {
    setRequesting(true);
    try {
      await api.post("/affiliate/payout-request", {});
      toast.success("Payout requested — we'll process it soon");
      api.get("/affiliate/me").then((r) => setMe(r.data)).catch(() => {});
      loadRequests();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to request payout"); }
    finally { setRequesting(false); }
  };

  const setField = (methodId, key, value) => {
    setDetails((d) => ({ ...d, [methodId]: { ...(d[methodId] || {}), [key]: value } }));
  };

  const savePayout = async (e) => {
    e.preventDefault();
    setSavingPayout(true);
    try {
      const { data } = await api.put("/affiliate/payout-profile", { details });
      setDetails(data.payout_details || {});
      toast.success("Payout details saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
    finally { setSavingPayout(false); }
  };

  if (loading) return <div className="p-24 text-center text-emerald-100/50">Loading…</div>;

  const status = me?.status;

  // Pending application
  if (!me?.is_affiliate && status === "pending") {
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center lg:px-8" data-testid="affiliate-pending">
        <Clock className="mx-auto text-amber-300" size={28} />
        <h1 className="mt-4 font-editorial text-3xl text-white">Application under review</h1>
        <p className="mt-3 text-sm text-emerald-100/70">Thanks for applying to the affiliate program. Our team is reviewing your application — you'll be notified once it's approved and your referral link goes live.</p>
        <Link to="/legal/affiliate-agreement" target="_blank" className="btn-edge-ghost mt-6 text-sm"><FileText size={14} /> Read the Affiliate Agreement</Link>
      </div>
    );
  }

  // No record yet, or previously rejected → show apply form
  if (!me?.is_affiliate) {
    const rejected = status === "rejected";
    return (
      <div className="mx-auto max-w-2xl px-5 py-20 lg:px-8" data-testid="affiliate-apply">
        <div className="text-center">
          <Share2 className="mx-auto text-lime-300" size={28} />
          <h1 className="mt-4 font-editorial text-3xl text-white">Become an affiliate</h1>
          <p className="mt-3 text-sm text-emerald-100/70">Earn commission on every trader you refer. Apply below — we review applications and activate your referral link once approved.</p>
        </div>
        {rejected && (
          <div className="mt-6 rounded-xl border border-amber-400/30 bg-amber-400/5 p-4 text-sm text-amber-200/90" data-testid="affiliate-rejected-note">
            Your previous application wasn't approved{me?.reject_note ? `: ${me.reject_note}` : "."} You're welcome to re-apply.
          </div>
        )}
        <div className="mt-6 card-moss p-6">
          <label className="flex items-start gap-3 text-sm text-emerald-100/80" data-testid="affiliate-agreement-label">
            <input type="checkbox" checked={acceptAgreement} onChange={(e) => setAcceptAgreement(e.target.checked)} data-testid="affiliate-agreement-check"
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-emerald-500/30 accent-lime-400" />
            <span>I have read and agree to the{" "}
              <Link to="/legal/affiliate-agreement" target="_blank" className="font-medium text-lime-300 hover:underline" data-testid="affiliate-agreement-link">Affiliate Agreement</Link>.
            </span>
          </label>
          <button onClick={apply} disabled={!acceptAgreement || applying} data-testid="affiliate-apply-submit"
            className="btn-edge mt-5 w-full justify-center text-sm disabled:opacity-50">
            {applying ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpRight size={14} />} Submit application
          </button>
        </div>
      </div>
    );
  }

  const link = `${window.location.origin}/?ref=${me.code}`;
  const s = me.stats || {};

  return (
    <div className="mx-auto max-w-6xl px-5 py-14 lg:px-8 lg:py-20" data-testid="affiliate-dashboard">
      <p className="overline mb-3">Affiliate program</p>
      <h1 className="font-editorial text-4xl font-medium tracking-tight text-white sm:text-5xl">Your referrals</h1>

      {me.needs_reaccept && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-amber-400/30 bg-amber-400/5 p-5" data-testid="affiliate-reaccept-banner">
          <div className="flex items-start gap-3">
            <ShieldAlert size={18} className="mt-0.5 text-amber-300" />
            <div>
              <p className="text-sm font-medium text-amber-100">The Affiliate Agreement has been updated</p>
              <p className="text-xs text-amber-200/70">Please review and accept the latest version to keep your affiliate account active. <Link to="/legal/affiliate-agreement" target="_blank" className="underline">Read it here</Link>.</p>
            </div>
          </div>
          <button onClick={reaccept} disabled={reaccepting} data-testid="affiliate-reaccept-btn" className="btn-edge text-sm">
            {reaccepting ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Accept updated agreement
          </button>
        </div>
      )}

      <div className="mt-6 card-moss p-6">
        <p className="overline mb-2">Your referral link · {me.commission_rate}% commission</p>
        <div className="flex flex-wrap items-center gap-2">
          <code className="flex-1 min-w-[240px] truncate rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-lime-300" data-testid="affiliate-link">{link}</code>
          <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }} data-testid="affiliate-copy-link" className="btn-edge-ghost text-sm"><Copy size={14} /> Copy link</button>
          <button onClick={() => { navigator.clipboard?.writeText(me.code); toast.success("Code copied"); }} data-testid="affiliate-copy-code" className="btn-edge-ghost text-sm">Code: {me.code}</button>
        </div>
        <p className="mt-3 text-xs text-emerald-100/50">Share the link, or give shoppers a coupon tied to your code. You earn on every paid order it brings in (self-purchases excluded).</p>
      </div>

      <form onSubmit={savePayout} className="mt-6 card-edge p-6" data-testid="affiliate-payout-profile">
        <div className="flex items-center gap-2"><Wallet size={16} className="text-lime-300" /><p className="overline">Payout details</p></div>
        <p className="mt-2 text-sm text-emerald-100/60">Tell us how you'd like to be paid. We pay out manually using the details below — keep them accurate. Only the methods we currently support are shown.</p>
        {methods.length === 0 ? (
          <p className="mt-4 text-sm text-emerald-100/50">No payout methods are enabled right now. Please check back later.</p>
        ) : (
          <>
            <div className="mt-5 grid gap-5 md:grid-cols-2">
              {methods.map((m) => (
                <div key={m.id} className="rounded-lg border border-emerald-500/12 bg-[#0d2416]/50 p-4" data-testid={`payout-method-${m.id}`}>
                  <p className="text-sm font-semibold text-white">{m.label}</p>
                  <div className="mt-3 grid gap-3">
                    {m.fields.map((f) => (
                      <div key={f.k}>
                        <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/50">{f.label}</label>
                        <input
                          type={f.type === "email" ? "email" : f.type === "tel" ? "tel" : "text"}
                          value={details[m.id]?.[f.k] || ""}
                          onChange={(e) => setField(m.id, f.k, e.target.value)}
                          data-testid={`payout-input-${m.id}-${f.k}`}
                          className="w-full rounded-md border border-emerald-500/15 bg-[#061510]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button disabled={savingPayout} data-testid="payout-save" className="btn-edge mt-5 text-sm">
              {savingPayout ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save payout details
            </button>
          </>
        )}
      </form>

      <div className="mt-6 grid gap-4 md:grid-cols-4">
        {[
          { k: "Referred orders", v: s.orders ?? 0, icon: Users },
          { k: "Total earned", v: money(s.earned_total), icon: DollarSign },
          { k: "Owed to you", v: money(s.owed), icon: Wallet },
          { k: "Paid out", v: money(s.paid_out), icon: DollarSign },
        ].map((c) => (
          <div key={c.k} className="card-edge p-5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-300"><c.icon size={16} /></span>
            <p className="overline mt-4">{c.k}</p>
            <p className="mt-1 font-editorial text-2xl text-white">{c.v}</p>
          </div>
        ))}
      </div>

      {(() => {
        const pending = requests.find((r) => r.status === "requested");
        const owed = Number(s.owed || 0);
        const hasDetails = Object.keys(details).length > 0;
        return (
          <div className="mt-6 card-moss flex flex-wrap items-center justify-between gap-4 p-6" data-testid="affiliate-payout-request">
            <div>
              <p className="overline mb-1">Request a payout</p>
              {pending ? (
                <p className="text-sm text-emerald-100/70">You have a pending request for <span className="font-editorial text-lime-300">{money(pending.amount)}</span>. We'll process it shortly.</p>
              ) : (
                <p className="text-sm text-emerald-100/70">You can request a payout once you're owed at least <span className="text-white">{money(minPayout)}</span>. {owed >= minPayout ? "You're eligible now." : `You currently have ${money(owed)}.`}</p>
              )}
            </div>
            {pending ? (
              <span className="chip chip-amber" data-testid="payout-request-pending">Pending review</span>
            ) : (
              <button onClick={requestPayout} disabled={requesting || owed < minPayout || !hasDetails}
                data-testid="payout-request-btn" className="btn-edge text-sm disabled:opacity-50"
                title={!hasDetails ? "Add payout details first" : owed < minPayout ? `Minimum ${money(minPayout)}` : "Request payout"}>
                {requesting ? <Loader2 size={14} className="animate-spin" /> : <Wallet size={14} />} Request {money(owed)}
              </button>
            )}
          </div>
        );
      })()}

      {requests.length > 0 && (
        <div className="mt-6 card-edge overflow-hidden" data-testid="affiliate-payout-requests-list">
          <div className="border-b border-emerald-500/10 px-6 py-4"><p className="overline">Payout requests</p></div>
          <div className="divide-y divide-emerald-500/8">
            {requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 px-6 py-3 text-sm" data-testid={`payout-request-${r.id}`}>
                <div>
                  <p className="text-white">{money(r.amount)}</p>
                  <p className="text-[11px] text-emerald-100/50">{new Date(r.created_at).toLocaleDateString()}{r.reject_note ? ` · ${r.reject_note}` : ""}</p>
                </div>
                <span className={`chip ${r.status === "paid" ? "chip-emerald" : r.status === "rejected" ? "chip-amber" : "chip-neutral"}`}>{r.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="card-edge overflow-hidden">
          <div className="border-b border-emerald-500/10 px-6 py-4"><p className="overline">Referral ledger</p></div>
          {refs.length === 0 ? <p className="p-6 text-sm text-emerald-100/50">No referred sales yet.</p> : (
            <div className="divide-y divide-emerald-500/8">
              {refs.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 px-6 py-3 text-sm" data-testid={`affiliate-ref-${r.id}`}>
                  <div className="min-w-0">
                    <p className="text-white">{money(r.commission_amount)} <span className="text-[11px] text-emerald-100/50">of {money(r.order_total)} · {r.source}</span></p>
                    <p className="truncate text-[11px] text-emerald-100/60">{(r.items || []).map((i) => i.title).join(", ") || "—"}</p>
                    <p className="text-[11px] text-emerald-100/40">{r.user_email} · {new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`chip ${r.status === "paid" ? "chip-emerald" : "chip-neutral"}`}>{r.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card-edge overflow-hidden">
          <div className="border-b border-emerald-500/10 px-6 py-4"><p className="overline">Payout history</p></div>
          {payouts.length === 0 ? <p className="p-6 text-sm text-emerald-100/50">No payouts yet.</p> : (
            <div className="divide-y divide-emerald-500/8">
              {payouts.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-6 py-3 text-sm">
                  <div>
                    <p className="text-white">{money(p.amount)} · {p.method}</p>
                    <p className="text-[11px] text-emerald-100/50">{p.reference || "—"} · {new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="chip chip-emerald">paid</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
